import { Input } from '@/common/components/Input.tsx'
import { useTranslation } from 'react-i18next'
import { z } from 'zod'
import { SubmitHandler, useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { useNavigate } from 'react-router-dom';
import { AppRoutePaths } from '@/routing/route_paths'
import { Label } from '@/shad/ui/label'
import { useSelector, useDispatch } from "react-redux"
import { RootState } from '@/storage/store'
import { setReimbursementAccountAddress } from '@/storage/applications/reimubrusementAccountSlice'
import FamilyWizardLayout from '@/app/pages/family_application/FamilyWizardLayout'
import { SubmitButton } from '@/app/pages/family_application/student_details/shared/submit-button'
import { CommonQueries } from '@/queries/common/queries'
import { US_STATES_ID } from '@/app/common/constants'
import { useQuery } from '@tanstack/react-query'

const detailSchema = z
  .object({
    address: z.string().min(1, 'address_is_required'),
    address2: z.string().optional(),
    city: z.string().min(1, 'city_is_required'),
    state: z.number({
      message: "state_is_required"
    }).min(1, 'state_is_required'),
    zipcode: z.string().min(1, 'zip_code_is_required').regex(/^\d{5}(-\d{4})?$/, {
      message: 'invalid_zip_format',
    })
  })

type FormValues = z.infer<typeof detailSchema>

export const ReimbursementAccountAddress = () => {
  const { t: translate } = useTranslation()
  const navigate = useNavigate()
  const dispatch = useDispatch()
  const { currentAccount } = useSelector((state: RootState) => state.ReimbursementAccount)
  const { data: statesData } = useQuery(
    CommonQueries.FetchStateByContry(US_STATES_ID)
  )

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<FormValues>({
    resolver: zodResolver(detailSchema),
    defaultValues: {
      address: currentAccount?.address?.address ?? "",
      address2: currentAccount?.address?.address2 ?? "",
      city: currentAccount?.address?.city ?? "",
      state: currentAccount?.address?.state ?? undefined,
      zipcode: currentAccount?.address?.zipcode ?? "",
    }
  })

  const onSubmit: SubmitHandler<FormValues> = async (data: FormValues) => {
    dispatch(setReimbursementAccountAddress({ address: data.address, address2: data.address2, city: data.city, state: data.state, zipcode: data.zipcode }))
    navigate(AppRoutePaths.reimbursements.accounts.link.account_number)
  }

  return (
    <FamilyWizardLayout>
      <FamilyWizardLayout.Header>
        <h1>{translate('whats_your_address')}</h1>
      </FamilyWizardLayout.Header>
      <FamilyWizardLayout.Content className="w-80">
        <form
          id="add-details-form"
          onSubmit={handleSubmit(onSubmit)}
          className="space-y-3"
        >
          <div className="my-10">
            <section
              className="flex flex-col mt-6 space-y-2"
              aria-labelledby="address_label"
            >
              <Label
                id="address_label"
                htmlFor="address"
              >
                {translate('address')}{' '}
                <span className="text-danger ml-1">*</span>
              </Label>
              <div className="relative mt-2 rounded-md shadow-sm">
                <Input
                  id="address"
                  {...register('address')}
                />
              </div>
              {errors.address && (
                <p className="mt-1 text-sm text-red-600">
                  {translate(errors.address.message ?? '')}
                </p>
              )}
            </section>
            <section
              className="flex flex-col mt-6 space-y-2"
              aria-labelledby="address2_label"
            >
              <Label
                id="address2_label"
                htmlFor="address2"
              >
                {translate('address_2')}
              </Label>
              <div className="relative mt-2 rounded-md shadow-sm">
                <Input
                  id="address2"
                  {...register('address2')}
                />
              </div>
            </section>
            <section
              className="flex flex-col mt-6 space-y-2"
              aria-labelledby="city_label"
            >
              <Label
                id="city_label"
                htmlFor="city"
              >
                {translate('city')}{' '}
                <span className="text-danger ml-1">*</span>
              </Label>
              <div className="relative mt-2 rounded-md shadow-sm">
                <Input
                  id="city"
                  {...register('city')}
                />
              </div>
              {errors.city && (
                <p className="mt-1 text-sm text-red-600">
                  {translate(errors.city.message ?? '')}
                </p>
              )}
            </section>
            <section
              className="flex flex-col mt-6 space-y-2"
              aria-labelledby="state_label"
            >
              <Label
                id="flex"
                htmlFor="state"
              >
                {translate('state')}{' '}
                <span className="text-danger ml-1">*</span>
              </Label>
              <div className="relative mt-2 rounded-md shadow-sm">
                <select
                  id="state"
                  {...register('state', { valueAsNumber: true })}
                  className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-inset focus:ring-primary-600 sm:text-sm sm:leading-6"
                >
                  <option value="">{translate('select_state')}</option>
                  {statesData?.data.map((state) => (
                    <option key={state.id} value={state.id}>
                      {state.name}
                    </option>
                  ))}
                </select>
              </div>
              {errors.state && (
                <p className="mt-1 text-sm text-red-600">
                  {translate(errors.state.message ?? '')}
                </p>
              )}
            </section>
            <section
              className="flex flex-col mt-6 space-y-2"
              aria-labelledby="zipcode_label"
            >
              <Label
                id="zipcode_label"
                htmlFor="zipcode"
              >
                {translate('zip_code')}{' '}
                <span className="text-danger ml-1">*</span>
              </Label>
              <div className="relative mt-2 rounded-md shadow-sm">
                <Input
                  id="zipcode"
                  {...register('zipcode')}
                />
              </div>
              {errors.zipcode && (
                <p className="mt-1 text-sm text-red-600">
                  {translate(errors.zipcode.message ?? '')}
                </p>
              )}
            </section>
          </div>
        </form>
      </FamilyWizardLayout.Content>
      <FamilyWizardLayout.ActionBar className="justify-center">
        <div className="flex">
          <SubmitButton
            form="add-details-form"
            status={'idle'}
            buttonTxt={translate('next')}
            className="px-10"
          />
        </div>
      </FamilyWizardLayout.ActionBar>
    </FamilyWizardLayout>
  )
}
